import { useCallback, useEffect, useState } from "react";
import { container } from "tsyringe";
import CommentResponseResource from "common/resources/Project/Config/Forum/Comment/CommentResponseResource";
import { isNonEmpty, upsert } from "common/utils/array";
import { EmptyComments } from "components/pages/Post/components/EmptyComments/EmptyComments";
import CommentService from "services/projects/CommentService";
import { usePostId } from "hooks/usePostId";
import { useProjectId } from "hooks/useProjectId";
import CommentForm from "./CommentForm";
import CommentCard from "./CommentCard";
import classes from "./classes.module.scss";

const commentService = container.resolve(CommentService);

export function CommentSection() {
	const { comments, removeComment, upsertComment } = useComments();

	return (
		<div className={classes["root"]}>
			<CommentForm onCreated={upsertComment} />
			{isNonEmpty(comments) ? (
				<div className={classes["comments"]}>
					{comments.map((comment) => (
						<CommentCard key={comment.id} comment={comment} onDeleted={removeComment} depth={0} />
					))}
				</div>
			) : (
				<EmptyComments />
			)}
		</div>
	);
}

const useComments = () => {
	const { projectId } = useProjectId();
	const { postId } = usePostId();
	const [comments, setComments] = useState<CommentResponseResource[]>([]);

	const fetchComments = useCallback(async () => {
		return commentService.getAll(projectId, postId).then(setComments);
	}, [projectId, postId]);

	const upsertComment = useCallback((comment: CommentResponseResource) => {
		setComments((comments) => upsert(comments, comment, (c) => c.id === comment.id, { prepend: true }));
		return comment;
	}, []);

	const removeComment = useCallback((deletedComment: Pick<CommentResponseResource, "id">) => {
		setComments((comments) => comments.filter((c) => c.id !== deletedComment.id));
		return deletedComment;
	}, []);

	useEffect(() => {
		fetchComments();
	}, [fetchComments]);

	return {
		comments,
		upsertComment,
		removeComment,
	};
};
